import {
  IncentiveStatus,
  Mutation,
  MutationOptInToIncentiveArgs,
  OptInToIncentiveError,
  OptInToIncentiveResultTypenames,
} from '@varo/graphql-schema-types';
import OptInToIncentive from 'services/graphql/mutations/Incentive/OptInToIncentive';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

export type OptInOutcome =
  | { status: IncentiveStatus; error?: never }
  | { status?: never; error?: OptInToIncentiveError };

const useOptInToIncentive = () => {
  const [{ fetching }, executeMutation] = useCancellableMutation<
    Mutation,
    MutationOptInToIncentiveArgs
  >(OptInToIncentive);

  const optInToIncentive = async (
    incentiveProgramId: string,
  ): Promise<OptInOutcome | undefined> => {
    try {
      const response = await executeMutation({
        input: { incentiveProgramId },
      });
      const result = response.data?.optInToIncentive;

      if (
        result?.__typename ===
        OptInToIncentiveResultTypenames.OptInToIncentiveSuccess
      ) {
        return { status: result.status };
      }

      return {
        error:
          result?.__typename ===
          OptInToIncentiveResultTypenames.OptInToIncentiveError
            ? result
            : undefined,
      };
    } catch (caughtError) {
      if (caughtError instanceof AbortError) {
        return undefined;
      }

      logBreadcrumb({
        level: 'error',
        message: 'optInToIncentive failed',
        data: { incentiveProgramId, error: caughtError },
      });
      return {};
    }
  };

  return { optInToIncentive, isOptingIn: fetching };
};

export default useOptInToIncentive;
